// SEO Utilities for StudyMind
export interface SEOConfig {
  title: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
}

export const DEFAULT_SEO: SEOConfig = {
  title: 'StudyMind - AI-Powered Learning Platform',
  description: 'Transform your learning with StudyMind\'s AI-powered study tools. Create smart flashcards, generate practice exams, get writing feedback, and master any subject with personalized learning experiences.',
  keywords: 'study tools, flashcards, AI learning, exam preparation, study platform, online learning, educational technology',
  image: '/study-mind-logo.svg',
  url: 'https://www.study-mind.com/'
};

export const updatePageTitle = (title: string): void => {
  document.title = title;
};

export const updateMetaDescription = (description: string): void => {
  let metaDescription = document.querySelector('meta[name="description"]');
  if (!metaDescription) {
    metaDescription = document.createElement('meta');
    metaDescription.setAttribute('name', 'description');
    document.head.appendChild(metaDescription);
  }
  metaDescription.setAttribute('content', description);
};

export const updateMetaKeywords = (keywords: string): void => {
  let metaKeywords = document.querySelector('meta[name="keywords"]');
  if (!metaKeywords) {
    metaKeywords = document.createElement('meta');
    metaKeywords.setAttribute('name', 'keywords');
    document.head.appendChild(metaKeywords);
  }
  metaKeywords.setAttribute('content', keywords);
};

export const updateOpenGraph = (config: Partial<SEOConfig>): void => {
  const { title, description, image, url } = config;
  
  if (title) {
    updateMetaTag('property', 'og:title', title);
  }
  
  if (description) {
    updateMetaTag('property', 'og:description', description);
  }
  
  if (image) {
    updateMetaTag('property', 'og:image', image);
  }
  
  if (url) {
    updateMetaTag('property', 'og:url', url);
  }
};

export const updateTwitterCard = (config: Partial<SEOConfig>): void => {
  const { title, description, image } = config;
  
  if (title) {
    updateMetaTag('name', 'twitter:title', title);
  }
  
  if (description) {
    updateMetaTag('name', 'twitter:description', description);
  }
  
  if (image) {
    updateMetaTag('name', 'twitter:image', image);
  }
};

const updateMetaTag = (attribute: string, value: string, content: string): void => {
  let metaTag = document.querySelector(`meta[${attribute}="${value}"]`);
  if (!metaTag) {
    metaTag = document.createElement('meta');
    metaTag.setAttribute(attribute, value);
    document.head.appendChild(metaTag);
  }
  metaTag.setAttribute('content', content);
};

export const updateSEO = (config: SEOConfig): void => {
  updatePageTitle(config.title);
  
  if (config.description) {
    updateMetaDescription(config.description);
  }
  
  if (config.keywords) {
    updateMetaKeywords(config.keywords);
  }
  
  updateOpenGraph(config);
  updateTwitterCard(config);
};

// Predefined SEO configurations for different pages
export const SEO_CONFIGS = {
  home: {
    title: 'StudyMind - AI-Powered Learning Platform | Flashcards, Exams & Study Tools',
    description: 'Transform your learning with StudyMind\'s AI-powered study tools. Create smart flashcards, generate practice exams, get writing feedback, and master any subject with personalized learning experiences.',
    keywords: 'study tools, flashcards, AI learning, exam preparation, study platform, online learning, educational technology, smart flashcards, practice exams, writing feedback, study notes, learning management'
  },
  login: {
    title: 'StudyMind - AI-Powered Learning Platform',
    description: 'Sign in to StudyMind and access your personalized learning dashboard with AI-powered study tools, flashcards, and exam preparation features.',
    keywords: 'login, sign in, study tools, AI learning, educational platform'
  },
  register: {
    title: 'Register - StudyMind | Join AI-Powered Learning Platform',
    description: 'Create your free StudyMind account and start your learning journey with AI-powered flashcards, exams, and study tools.',
    keywords: 'register, sign up, free account, study tools, AI learning, educational platform'
  },
  flashcards: {
    title: 'Flashcards - StudyMind | AI-Generated Smart Flashcards',
    description: 'Create and study AI-generated flashcards with StudyMind. Smart learning algorithms adapt to your progress and help you master any subject.',
    keywords: 'flashcards, AI flashcards, smart learning, study cards, memory training, spaced repetition'
  },
  exams: {
    title: 'Practice Exams - StudyMind | AI-Generated Test Questions',
    description: 'Generate personalized practice exams with StudyMind\'s AI. Get instant feedback and track your progress with comprehensive exam preparation tools.',
    keywords: 'practice exams, test questions, exam preparation, AI-generated questions, study tests, assessment tools'
  },
  writing: {
    title: 'Writing Feedback - StudyMind | AI Writing Assessment & Grading',
    description: 'Get instant AI-powered writing feedback and grading with StudyMind. Improve your essays, reports, and academic writing with detailed analysis.',
    keywords: 'writing feedback, essay grading, AI writing assessment, writing improvement, academic writing, essay help'
  },
  notes: {
    title: 'Study Notes - StudyMind | AI-Generated Learning Notes',
    description: 'Transform your study materials into organized, AI-generated notes with StudyMind. Create comprehensive study guides for any subject.',
    keywords: 'study notes, learning notes, AI notes, study guides, educational content, knowledge organization'
  },
  understanding: {
    title: 'Understanding Chat - StudyMind | AI Learning Assistant',
    description: 'Ask questions and get instant answers with StudyMind\'s AI learning assistant. Upload images and get detailed explanations for any topic.',
    keywords: 'AI chat, learning assistant, question answering, educational AI, study help, tutoring'
  },
  listening: {
    title: 'Listening Practice - StudyMind | Audio Learning & Assessment',
    description: 'Improve your listening skills with StudyMind\'s audio learning tools. Practice with uploaded audio files and get instant feedback.',
    keywords: 'listening practice, audio learning, language learning, listening comprehension, audio assessment'
  },
  profile: {
    title: 'Profile - StudyMind | Your Learning Dashboard',
    description: 'Manage your StudyMind profile, track your learning progress, and view your study statistics and achievements.',
    keywords: 'user profile, learning dashboard, progress tracking, study statistics, achievements'
  }
}; 